import React, { useState, useEffect } from 'react';
import { Bell, Smartphone, X, AlertCircle, Download } from 'lucide-react';
import { pwaService } from '../services/pwaService';
import { Button, Group, Stack, Text, Card, Badge, Alert } from '@mantine/core';
import { useUser } from '../contexts/UserContext';

interface Device {
  deviceId: string;
  deviceName: string;
  createdAt: string;
  lastUsed?: string;
}

interface PushNotificationSetupProps {
  onComplete?: () => void;
}

export const PushNotificationSetup: React.FC<PushNotificationSetupProps> = ({ onComplete }) => {
  const { currentUser } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [devices, setDevices] = useState<Device[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [currentDeviceId, setCurrentDeviceId] = useState<string>('');

  useEffect(() => {
    checkCurrentStatus();
    loadDevices();
  }, [currentUser]);

  const checkCurrentStatus = () => {
    setNotificationPermission(pwaService.getNotificationPermission());
    setIsSubscribed(pwaService.isPushNotificationsEnabled());
    setIsPWAInstalled(pwaService.isAppInstalled());
    setShowInstallPrompt(pwaService.isInstallPromptAvailable());
    setCurrentDeviceId(pwaService.getCurrentDeviceId());
  };

  const loadDevices = async () => {
    try {
      const userDevices = await pwaService.getUserDevices();
      setDevices(userDevices);
    } catch (error) {
      console.error('Failed to load devices:', error);
    }
  };

  const handleInstallPWA = async () => {
    setIsLoading(true);
    try {
      const success = await pwaService.installApp();
      if (success) {
        setIsPWAInstalled(true);
        setShowInstallPrompt(false);
        // After installation, automatically prompt for notifications
        setTimeout(() => {
          handleEnableNotifications();
        }, 1000);
      }
    } catch (error) {
      setError('Failed to install app. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnableNotifications = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const success = await pwaService.subscribeToPushNotifications();
      if (success) {
        setIsSubscribed(true);
        setNotificationPermission('granted');
        await loadDevices(); // Refresh device list
        onComplete?.();
      } else {
        setError('Failed to enable notifications. Please check your browser settings.');
      }
    } catch (error: any) {
      if (error.message === 'iOS_PWA_REQUIRED') {
        setError('Please install the app first to enable notifications on iOS.');
      } else {
        setError('Failed to enable notifications. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setIsLoading(true);
    try {
      const success = await pwaService.unsubscribeFromPushNotifications();
      if (success) {
        setIsSubscribed(false);
        await loadDevices(); // Refresh device list
      }
    } catch (error) {
      setError('Failed to disable notifications.');
    } finally {
      setIsLoading(false);
    }
  };

  const otherDevices = devices.filter(d => d.deviceId !== currentDeviceId);

  // iOS specific messaging
  const iosStatus = pwaService.getIOSPushNotificationStatus();
  const iosMessage = pwaService.getPushNotificationStatusMessage();

  return (
    <Stack gap="md">
      <div className="settings-section-header">
        <h2 className="settings-section-title">Push Notifications</h2>
        <p className="settings-section-description">
          Get notified instantly about task assignments, updates, and reminders on all your devices.
        </p>
      </div>

      {error && (
        <Alert icon={<AlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      {iosStatus === 'requires-pwa' && (
        <Alert icon={<AlertCircle size={16} />} color="orange" variant="light">
          {iosMessage}
        </Alert>
      )}

      {/* Current Device Status */}
      <Card withBorder padding="md">
        <Group justify="space-between" align="flex-start">
          <div style={{ flex: 1 }}>
            <Group gap="sm" mb="xs">
              <Smartphone size={16} />
              <Text fw={500}>This Device</Text>
              <Badge variant="light" color="blue" size="sm">
                {pwaService.getCurrentDeviceName()}
              </Badge>
            </Group>
            
            <Text size="sm" c="dimmed" mb="md">
              {isSubscribed 
                ? "✅ Notifications enabled - you'll receive push notifications on this device"
                : notificationPermission === 'denied'
                  ? "❌ Notifications blocked - please enable them in your browser settings"
                  : !isPWAInstalled && iosStatus === 'requires-pwa'
                    ? "📱 Install the app first to enable notifications"
                    : "🔔 Enable notifications to stay updated on this device"
              }
            </Text>

            {/* Install PWA Button (iOS Safari or installable) */}
            {(!isPWAInstalled && (showInstallPrompt || iosStatus === 'requires-pwa')) && (
              <Button
                leftSection={<Download size={16} />}
                onClick={handleInstallPWA}
                loading={isLoading}
                variant="light"
                color="blue"
                size="sm"
                mb="sm"
              >
                Install App
              </Button>
            )}

            {/* Notification Control Buttons */}
            {(isPWAInstalled || iosStatus === 'supported') && (
              <Group gap="sm">
                {notificationPermission === 'denied' ? (
                  <Button variant="light" color="gray" size="sm" disabled>
                    Blocked in Browser
                  </Button>
                ) : isSubscribed ? (
                  <Button
                    leftSection={<X size={16} />}
                    onClick={handleDisableNotifications}
                    loading={isLoading}
                    variant="light"
                    color="red"
                    size="sm"
                  >
                    Disable Notifications
                  </Button>
                ) : (
                  <Button
                    leftSection={<Bell size={16} />}
                    onClick={handleEnableNotifications}
                    loading={isLoading}
                    variant="filled"
                    color="blue"
                    size="sm"
                  >
                    Enable Notifications
                  </Button>
                )}
              </Group>
            )}
          </div>
        </Group>
      </Card>

      {/* Other Devices */}
      {otherDevices.length > 0 && (
        <div>
          <Text size="md" fw={500} mb="sm" c="var(--app-text-primary)">Other Devices</Text>
          <Stack gap="xs">
            {otherDevices.map((device) => (
              <Card key={device.deviceId} withBorder padding="sm">
                <Group justify="space-between">
                  <div>
                    <Group gap="sm">
                      <Smartphone size={14} />
                      <Text size="sm" fw={500}>{device.deviceName}</Text>
                      <Badge variant="light" color="green" size="xs">
                        Active
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      Last used: {device.lastUsed 
                        ? new Date(device.lastUsed).toLocaleDateString()
                        : new Date(device.createdAt).toLocaleDateString()
                      }
                    </Text>
                  </div>
                </Group>
              </Card>
            ))}
          </Stack>
        </div>
      )}

      {/* Setup Instructions */}
      {devices.length === 0 && !isSubscribed && (
        <Card withBorder padding="md" style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
          <Text size="sm" fw={500} mb="xs">🚀 Quick Setup</Text>
          <Text size="sm" c="dimmed">
            1. Install the app on each device you use<br/>
            2. Enable notifications when prompted<br/>
            3. Stay updated across all your devices!
          </Text>
        </Card>
      )}
    </Stack>
  );
};

export default PushNotificationSetup;